import axios from 'axios-https-proxy-fix'
import storeJS from 'store'
import { setRefreshedTokens } from '../fetchingfunction/user/user.action'
// Use safer import approach
// Ensure store is properly initialized with fallback mechanism
const store = storeJS || {
  get: (key) => localStorage.getItem(key),
  set: (key, value) => localStorage.setItem(key, value),
  remove: (key) => localStorage.removeItem(key),
}

// Update API URL to use localhost with HTTP on port 8090
const API_URL = process.env.REACT_APP_API_URL
// const API_URL = 'https://backend-staging.apolop2p.com/api/v1/'

// const clientId = '3be4f5444c2868d20996a4ec31d44902.access'
// const client_secret = '9cbc6c51b0ec8d40f463022236bcb4cfedd6da622c0ad9b32319bc5371c0d403'

const axiosApi = axios.create({
  baseURL: API_URL,
  // For local development, we use HTTP
  withCredentials: false, // Don't send cookies with cross-site requests
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

// Add a request interceptor to log requests
axiosApi.interceptors.request.use((request) => {
  return request
})

// Add a response interceptor to log responses and handle errors
axiosApi.interceptors.response.use(
  (response) => {
    // console.log('Response:', response)
    return response
  },
  (error) => {
    // console.error('Error Response:', error.response || error)
    return Promise.reject(error)
  }
)

// Define public paths that don't need authentication
const PUBLIC_PATHS = [
  // Auth-related endpoints
  '/user/register/',
  '/user/login/',
  '/activate/',
  '/user/password/reset/',
  '/user/password-reset/confirm/',

  // Common data endpoints that don't require authentication
  '/address/country/',
  '/address/language/',
  '/address/timezone/',
  '/address/available_countries/',
  '/class_search/',
  '/job_search/',
]

// Define activation-related paths that should skip token validation
// This prevents redirect loops during the activation process
const ACTIVATION_PATHS = ['/activate/']

// Add global refresh counter near the top of the file, outside any functions
let refreshAttemptCount = 0
const MAX_REFRESH_ATTEMPTS = 2 // Maximum number of refresh attempts before forcing logout

// Export a setup function instead of applying interceptor directly
export function setupAxiosInterceptors(reduxStore) {
  const refreshTokenUrlPath = '/user/refresh-token/' // Path part for checking

  // Reset attempt counter when setting up interceptors
  refreshAttemptCount = 0

  axiosApi.interceptors.request.use((req) => {
    // console.log('[Request Interceptor] Starting for URL:', req.url)
    req.headers = req.headers || {}
    // req.headers['CF-Access-Client-Id'] = clientId
    // req.headers['CF-Access-Client-Secret'] = client_secret
    let accessToken
    let tokenSource = 'None'

    // Log initial state
    // console.log(
    //   '[Request Interceptor] Initial check - localStorage token:',
    //   localStorage.getItem('access_token')
    // )
    // console.log('[Request Interceptor] Initial check - store.js token:', store.get('access_token'))
    if (reduxStore && typeof reduxStore.getState === 'function') {
      try {
        // console.log(
        //   '[Request Interceptor] Initial check - Redux state token:',
        //   reduxStore.getState().user?.accessToken
        // )
      } catch (e) {
        // console.error('[Request Interceptor] Error accessing Redux state for logging:', e)
      }
    }

    // Improved token handling to prevent 401 errors
    const fixTokenQuotes = () => {
      // Helper to clean and validate token
      const cleanAndValidateToken = (token) => {
        if (!token) return null

        // If it's already a non-string type, leave it
        if (typeof token !== 'string') return token

        // If token is the string 'null' or 'undefined', treat as null
        if (token === 'null' || token === 'undefined') return null

        // Remove quotes if present
        let cleanToken = token
        if (token.startsWith('"') && token.endsWith('"')) {
          cleanToken = token.substring(1, token.length - 1)
        }

        // If cleaned token is valid JWT format (at least has two periods), return it
        if (cleanToken && cleanToken.split('.').length >= 3) {
          return cleanToken
        }

        // If doesn't look like a valid token, log and return null
        // console.log(
        //   `[Request Interceptor] Invalid token found: ${cleanToken.substring(0, 10)}... - clearing`
        // )
        return null
      }

      // Fix access token in localStorage
      const localToken = localStorage.getItem('access_token')
      const cleanLocalToken = cleanAndValidateToken(localToken)
      if (cleanLocalToken !== localToken) {
        // console.log('[Request Interceptor] Fixing access token in localStorage')
        if (cleanLocalToken) {
          localStorage.setItem('access_token', cleanLocalToken)
        } else {
          localStorage.removeItem('access_token')
        }
      }

      // Fix access token in store.js
      const storeToken = store.get('access_token')
      const cleanStoreToken = cleanAndValidateToken(storeToken)
      if (cleanStoreToken !== storeToken) {
        // console.log('[Request Interceptor] Fixing access token in store.js')
        if (cleanStoreToken) {
          store.set('access_token', cleanStoreToken)
        } else {
          store.remove('access_token')
        }
      }

      // Fix refresh token in localStorage
      const localRefreshToken = localStorage.getItem('refresh_token')
      const cleanLocalRefreshToken = cleanAndValidateToken(localRefreshToken)
      if (cleanLocalRefreshToken !== localRefreshToken) {
        // console.log('[Request Interceptor] Fixing refresh token in localStorage')
        if (cleanLocalRefreshToken) {
          localStorage.setItem('refresh_token', cleanLocalRefreshToken)
        } else {
          localStorage.removeItem('refresh_token')
        }
      }

      // Fix refresh token in store.js
      const storeRefreshToken = store.get('refresh_token')
      const cleanStoreRefreshToken = cleanAndValidateToken(storeRefreshToken)
      if (cleanStoreRefreshToken !== storeRefreshToken) {
        // console.log('[Request Interceptor] Fixing refresh token in store.js')
        if (cleanStoreRefreshToken) {
          store.set('refresh_token', cleanStoreRefreshToken)
        } else {
          store.remove('refresh_token')
        }
      }
    }

    // Run the quote fixing function on each request
    fixTokenQuotes()

    // New order:
    // 1. Check for temporary token during form submission first (highest priority)
    if (!accessToken) {
      accessToken = window.tempAuthToken || window.currentSubmissionToken
      if (accessToken) tokenSource = 'Window (tempAuthToken/currentSubmissionToken)'
    }

    // 2. Try Redux store if available and no window token
    if (!accessToken && reduxStore && typeof reduxStore.getState === 'function') {
      try {
        accessToken = reduxStore.getState().user?.accessToken
        if (accessToken) tokenSource = 'Redux Store'
      } catch (e) {
        // console.error('[Request Interceptor] Error accessing Redux state for accessToken:', e)
      }
    }

    // 3. If no temp token or Redux token, check localStorage
    if (!accessToken) {
      accessToken = localStorage.getItem('access_token')
      if (accessToken) tokenSource = 'localStorage'
    }

    // 4. If still no token, check cookie
    if (!accessToken) {
      const tokenCookie = document.cookie.split('; ').find((row) => row.startsWith('access_token='))
      if (tokenCookie) {
        accessToken = tokenCookie.split('=')[1]
        if (accessToken) tokenSource = 'Cookie'
      }
    }

    // 5. Last resort - check store.js (the library)
    if (!accessToken) {
      const storeToken = store.get('access_token')
      if (storeToken) {
        accessToken = storeToken
        if (accessToken) tokenSource = 'store.js'
      }
    }

    // console.log(
    //   `[Request Interceptor] Token determined. Source: ${tokenSource}, Token (first 10 chars): ${
    //     accessToken ? accessToken.substring(0, 10) + '...' : 'N/A'
    //   }`
    // )

    // Trim leading/trailing quotes from the accessToken if it exists
    let cleanedAccessToken = accessToken
    if (typeof cleanedAccessToken === 'string') {
      // EXTREME TOKEN CLEANING: Handle severely over-escaped tokens
      // This addresses cases where tokens have been JSON.stringify'd multiple times
      // console.log(
      //   '[Request Interceptor] Cleaning token, original length:',
      //   cleanedAccessToken.length
      // )

      // First handle extreme cases with multiple layers of escaping
      if (
        cleanedAccessToken.includes('\\') ||
        (cleanedAccessToken.startsWith('"') && cleanedAccessToken.includes('\\"'))
      ) {
        // console.log('[Request Interceptor] Detected heavily escaped token - using deep cleaning')

        // Try recursive JSON parsing to remove layers of stringification
        let parsedToken = cleanedAccessToken
        let lastLength = parsedToken.length
        let iterations = 0
        const maxIterations = 10 // Safety limit

        try {
          while (iterations < maxIterations) {
            iterations++
            // Stop if we've removed all quotes and escapes
            if (!parsedToken.startsWith('"') && !parsedToken.includes('\\')) {
              break
            }

            // Try to parse this layer
            if (parsedToken.startsWith('"')) {
              try {
                const attemptParse = JSON.parse(parsedToken)
                if (typeof attemptParse === 'string') {
                  parsedToken = attemptParse
                  // console.log(
                  //   `[Request Interceptor] Parsed layer ${iterations}, new length:`,
                  //   parsedToken.length
                  // )

                  // If we didn't make progress, break to avoid infinite loop
                  if (parsedToken.length === lastLength) {
                    break
                  }
                  lastLength = parsedToken.length
                } else {
                  // If parsing didn't return a string, we're done
                  break
                }
              } catch (e) {
                // If JSON parsing fails, break out and try the regex approach
                // console.log(
                //   `[Request Interceptor] JSON parse failed at iteration ${iterations}:`,
                //   e.message
                // )
                break
              }
            } else {
              // If no more leading quotes, we're likely done with JSON parsing
              break
            }
          }

          if (iterations >= maxIterations) {
            // console.warn('[Request Interceptor] Hit max iterations trying to parse token')
          }

          // After JSON parsing attempts, remove any remaining escapes with regex
          // This gets rid of any remaining backslashes and quotes
          cleanedAccessToken = parsedToken.replace(/\\+"/g, '').replace(/^"|"$/g, '')

          // For extreme cases where the token got fully mangled, try to extract the JWT pattern directly
          const jwtPattern = /eyJ[a-zA-Z0-9_-]+\.eyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+/
          const jwtMatch = cleanedAccessToken.match(jwtPattern)
          if (jwtMatch && jwtMatch[0]) {
            cleanedAccessToken = jwtMatch[0]
            // console.log('[Request Interceptor] Extracted JWT pattern directly')
          }

          // console.log(
          //   `[Request Interceptor] After deep cleaning, token length: ${cleanedAccessToken.length}`
          // )
        } catch (e) {
          // console.error('[Request Interceptor] Error during deep token cleaning:', e)
          // Fallback to original token if deep cleaning fails completely
          cleanedAccessToken = accessToken
        }
      } else {
        // For simpler cases, use the existing cleaning logic
        // First, try a JSON parse if it looks like a stringified string
        if (cleanedAccessToken.startsWith('"') && cleanedAccessToken.endsWith('"')) {
          try {
            const possiblyParsed = JSON.parse(cleanedAccessToken)
            if (typeof possiblyParsed === 'string') {
              cleanedAccessToken = possiblyParsed
              console.log('[Request Interceptor] Successfully JSON parsed token string')
            }
          } catch (e) {
            // If JSON.parse fails, continue with the basic cleaning below
            // console.log('[Request Interceptor] Token was quoted but not JSON parseable')
          }
        }

        // Then try basic string trimming for any remaining quotes
        // Repeatedly trim until no more leading/trailing quotes are found
        while (cleanedAccessToken.startsWith('"') && cleanedAccessToken.endsWith('"')) {
          cleanedAccessToken = cleanedAccessToken.substring(1, cleanedAccessToken.length - 1)
        }

        // Also remove any escaped quotes that might remain
        cleanedAccessToken = cleanedAccessToken.replace(/\\"/g, '"')
      }

      if (cleanedAccessToken !== accessToken) {
        // console.log(
        //   `[Request Interceptor] Cleaned accessToken. Original length: ${accessToken.length}, Cleaned length: ${cleanedAccessToken.length}`
        // )

        // CRITICAL FIX: Store the properly cleaned token back to localStorage and store.js
        // This prevents the token from remaining in its corrupted state
        if (accessToken === localStorage.getItem('access_token')) {
          // console.log('[Request Interceptor] Correcting corrupted token in localStorage')
          localStorage.setItem('access_token', cleanedAccessToken)
        }

        if (accessToken === store.get('access_token')) {
          // console.log('[Request Interceptor] Correcting corrupted token in store.js')
          store.set('access_token', cleanedAccessToken)
        }
      }
    }

    // Improved check for public paths that handles partial matches better
    const urlWithoutParams = req.url.split('?')[0]

    // Special case for registration endpoint - ALWAYS check both with and without trailing slash
    const isRegisterPath =
      req.url.includes('user/register/?') ||
      urlWithoutParams === 'user/register/' ||
      urlWithoutParams === 'user/register'

    if (isRegisterPath) {
      // console.log(
      //   `[Request Interceptor] Register endpoint detected: ${req.url} - treating as public`
      // )
      if (req?.headers) {
        delete req.headers.Authorization
        delete req.headers.ApoloappAuth
      }
      return req // Return early for register endpoint
    }

    const isPublicPath = PUBLIC_PATHS.some((path) => {
      // Check if the URL starts with the public path
      // or if the URL path part (without query params) matches
      return urlWithoutParams.startsWith(path) || path.startsWith(urlWithoutParams)
    })

    // Check if this is an activation-related path that should skip token validation
    const isActivationPath = ACTIVATION_PATHS.some((path) => {
      return urlWithoutParams.includes(path)
    })

    // Check if this is the refresh token request itself
    const isRefreshTokenRequest = req.url.includes(refreshTokenUrlPath)

    // Set the auth header if we have a token and we're not accessing a public path
    // AND it's not the refresh token request itself

    if (req?.headers && cleanedAccessToken && !isPublicPath && !isRefreshTokenRequest) {
      // FINAL FIX: Ensure one last time that there are no quotes in the token used for the header
      // This header format should be: ApoloappAuth: Authorization eyJhbGc...etc (no quotes, no "Bearer")
      const finalCleanToken =
        typeof cleanedAccessToken === 'string'
          ? cleanedAccessToken.replace(/^"|"$/g, '')
          : cleanedAccessToken

      req.headers.ApoloappAuth = `Authorization ${finalCleanToken}`
      delete req.headers.Authorization // Remove legacy Authorization header
      // console.log(
      //   `[Request Interceptor] Adding ApoloappAuth header for: ${
      //     req.url
      //   }. Value: Authorization ${finalCleanToken.substring(0, 10)}... Token from: ${tokenSource}`
      // )
    } else if (isRefreshTokenRequest) {
      // console.log(
      //   `[Request Interceptor] Processing refresh token request, ensuring no Auth/ApoloappAuth header: ${req.url}`
      // )
      // Ensure no auth header is present for the refresh token request
      if (req?.headers?.Authorization) {
        delete req.headers.Authorization
      }
      if (req?.headers?.ApoloappAuth) {
        delete req.headers.ApoloappAuth
      }
    } else if (!isPublicPath && !cleanedAccessToken && !isActivationPath) {
      // Only warn about missing tokens for authenticated endpoints
      // Skip warning for activation paths to avoid noisy logs
      // console.warn(
      //   `[Request Interceptor] No auth token available for request to: ${req.url} (after all checks)`
      // )
    } else if (isPublicPath) {
      // Remove any auth headers for public paths to avoid token leakage
      if (req?.headers?.Authorization) {
        delete req.headers.Authorization
      }
      if (req?.headers?.ApoloappAuth) {
        delete req.headers.ApoloappAuth
      }
    }

    // If we're activating, ensure we don't have conflicting headers
    if (isActivationPath) {
      // console.log(`[Request Interceptor] Processing activation request: ${req.url}`)
      // For activation requests, don't include old tokens that might be invalid
      if (req?.headers?.Authorization && req.url.includes('/activate/')) {
        delete req.headers.Authorization
      }
      if (req?.headers?.ApoloappAuth && req.url.includes('/activate/')) {
        delete req.headers.ApoloappAuth
      }
    }
    return req
  })

  axiosApi.interceptors.response.use(
    (response) => response, // Pass through successful responses
    async (error) => {
      const originalRequest = error.config
      const fullRefreshTokenUrl = `${API_URL}${refreshTokenUrlPath}`
      // console.log(
      //   '[Response Interceptor] Error received:',
      //   error.response?.status,
      //   'for URL:',
      //   originalRequest.url
      // )

      // Only attempt token refresh if we haven't exceeded max attempts
      // Handle 401 errors by trying to refresh token, except for specific endpoints
      // where we know the user might not be authenticated yet
      const skipRefreshPaths = [
        '/user/login/',
        '/user/register/',
        '/activate/',
        '/reset-password/',
        '/forgot-password/',
      ]

      // Check if the current path is in the skip list
      const currentPath = originalRequest.url.replace(API_URL, '')
      const shouldSkipRefresh = skipRefreshPaths.some((path) => currentPath.includes(path))

      if (
        error.response?.status === 401 &&
        !shouldSkipRefresh &&
        originalRequest.url !== fullRefreshTokenUrl &&
        !originalRequest._retry &&
        refreshAttemptCount < MAX_REFRESH_ATTEMPTS
      ) {
        // console.log(
        //   `[Response Interceptor] Condition for token refresh met (401, not refresh URL, not retry). Attempt #${
        //     refreshAttemptCount + 1
        //   }`
        // )

        refreshAttemptCount++ // Increment counter
        originalRequest._retry = true

        let currentRefreshToken
        let refreshTokenSource = 'None'

        // Try Redux store first for refresh token
        if (reduxStore && typeof reduxStore.getState === 'function') {
          try {
            currentRefreshToken = reduxStore.getState().user?.refreshToken
            if (currentRefreshToken) refreshTokenSource = 'Redux Store'
          } catch (e) {
            // console.error('[Response Interceptor] Error accessing Redux state for refreshToken:', e)
          }
        }

        // Then try localStorage if not found in Redux
        if (!currentRefreshToken) {
          currentRefreshToken = localStorage.getItem('refresh_token')
          if (currentRefreshToken) refreshTokenSource = 'localStorage'
        }

        // Then try store.js (the library) if not found in localStorage
        if (!currentRefreshToken) {
          currentRefreshToken = store.get('refresh_token')
          if (currentRefreshToken) refreshTokenSource = 'store.js'
        }

        // console.log(
        //   `[Response Interceptor] Current Refresh Token Source: ${refreshTokenSource}, Token (first 10 chars): ${
        //     currentRefreshToken ? currentRefreshToken.substring(0, 10) + '...' : 'N/A'
        //   }`
        // )

        if (currentRefreshToken) {
          try {
            // console.log('[Response Interceptor] Attempting to refresh token.')
            // console.log('[Response Interceptor] Refresh URL:', fullRefreshTokenUrl)
            // console.log(
            //   '[Response Interceptor] Refresh token being used (first 10 chars):',
            //   currentRefreshToken ? currentRefreshToken.substring(0, 10) + '...' : 'N/A'
            // )
            const payloadForRefresh = { refresh: currentRefreshToken }
            // console.log(
            //   '[Response Interceptor] Payload for refresh:',
            //   JSON.stringify(payloadForRefresh)
            // )

            const refreshResponse = await axiosApi.post(fullRefreshTokenUrl, payloadForRefresh)
            // console.log(
            //   '[Response Interceptor] Token refresh API call responded:',
            //   refreshResponse.status,
            //   refreshResponse.data
            // )

            const newAccessTokenAPI = refreshResponse.data.access
            const newRefreshTokenAPI = refreshResponse.data.refresh

            if (newAccessTokenAPI) {
              // console.log(
              //   '[Response Interceptor] Token refreshed successfully. New Access Token (first 10 chars):',
              //   newAccessTokenAPI.substring(0, 10) + '...'
              // )

              // Reset counter on successful refresh
              refreshAttemptCount = 0

              const cleanNewAccessToken =
                typeof newAccessTokenAPI === 'string'
                  ? newAccessTokenAPI.replace(/^"|"$/g, '')
                  : newAccessTokenAPI

              // Store tokens consistently across all storage mechanisms
              localStorage.setItem('access_token', cleanNewAccessToken)
              store.set('access_token', cleanNewAccessToken)

              let finalRefreshToken = currentRefreshToken // Default to the one used for refresh
              if (newRefreshTokenAPI) {
                const cleanNewRefreshToken =
                  typeof newRefreshTokenAPI === 'string'
                    ? newRefreshTokenAPI.replace(/^"|"$/g, '')
                    : newRefreshTokenAPI
                localStorage.setItem('refresh_token', cleanNewRefreshToken)
                store.set('refresh_token', cleanNewRefreshToken)
                finalRefreshToken = cleanNewRefreshToken
                // console.log('[Response Interceptor] New Refresh Token also updated in storages.')
              }

              if (reduxStore && typeof reduxStore.dispatch === 'function') {
                // console.log('[Response Interceptor] Dispatching setRefreshedTokens to Redux store.')
                reduxStore.dispatch(
                  setRefreshedTokens({
                    access: cleanNewAccessToken,
                    refresh: finalRefreshToken,
                  })
                )
              } else {
                console.warn(
                  '[Response Interceptor] Redux store not available for dispatching token update.'
                )
              }

              originalRequest.headers['ApoloappAuth'] = `Authorization ${cleanNewAccessToken}`
              if (originalRequest.headers['Authorization']) {
                delete originalRequest.headers['Authorization']
              }
              axiosApi.defaults.headers.common.ApoloappAuth = `Authorization ${cleanNewAccessToken}`
              if (axiosApi.defaults.headers.common.Authorization) {
                delete axiosApi.defaults.headers.common.Authorization
              }

              // console.log(
              //   '[Response Interceptor] Retrying original request with new token:',
              //   originalRequest.url
              // )
              return axiosApi(originalRequest)
            } else {
              console.warn(
                '[Response Interceptor] Refresh response did not contain a new access token. Treating as refresh failure.'
              )
              throw new Error('Refresh API call did not return an access token.') // This will be caught by the catch block below
            }
          } catch (refreshError) {
            console.error(
              '[Response Interceptor] Token refresh API call FAILED or new token missing. Error:',
              refreshError.response?.status,
              refreshError.response?.data || refreshError.message
            )
            // console.log(
            //   '[Response Interceptor] Clearing tokens from localStorage and store.js due to refresh failure.'
            // )

            // Clear all tokens and user data
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            store.remove('access_token')
            store.remove('refresh_token')
            store.remove('user_data')

            if (reduxStore && typeof reduxStore.dispatch === 'function') {
              // Force logout after multiple failures
              if (refreshAttemptCount >= MAX_REFRESH_ATTEMPTS) {
                // console.log(
                //   `[Response Interceptor] Max refresh attempts (${MAX_REFRESH_ATTEMPTS}) reached. Forcing logout.`
                // )

                // Force logout after small delay to allow UI to update
                setTimeout(() => {
                  window.location.href = '/login'
                }, 100)
              }
            }
            return Promise.reject(refreshError)
          }
        } else {
          console.warn('[Response Interceptor] No refresh token found. Cannot attempt refresh.')
          // No refresh token, so the original 401 error should propagate.
        }
      } else if (error.response?.status === 401 && refreshAttemptCount >= MAX_REFRESH_ATTEMPTS) {
        // We've hit max attempts, force logout
        // console.log(
        //   `[Response Interceptor] Max refresh attempts (${MAX_REFRESH_ATTEMPTS}) reached with continued 401 errors. Forcing logout.`
        // )

        // Clear tokens
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        store.remove('access_token')
        store.remove('refresh_token')
        store.remove('user_data')

        // Force logout after small delay
        setTimeout(() => {
          window.location.href = '/login'
        }, 100)

        return Promise.reject(error)
      }

      // If error is not a 401, or it is the refresh URL, or already retried, or no refresh token was available for retry
      // console.log(
      //   '[Response Interceptor] Error not handled by refresh logic (or refresh failed & error propagated). Original error for URL:',
      //   originalRequest.url,
      //   error.response?.status
      // )
      return Promise.reject(error)
    }
  )
}

/**
 * @param {'GET' | 'POST' | 'PUT' | 'DELETE'} method
 * @param {string} url
 * @param {Record<string, any>?} data
 * @param {Record<string, string>?} params
 * @param {any?} headers
 */
const request = (method, url, data = null, params, headers) => {
  return axiosApi
    .request({
      headers,
      method,
      url,
      data,
      params,
    })
    .then((response) => {
      // console.log(`Request successful: ${method} ${url}`, response) // Logging successful response

      // Process the response to clean any tokens
      if (response?.data?.data) {
        // Auth endpoints that return tokens
        if (
          url.includes('/login/') ||
          url.includes('/activate/') ||
          url.includes('/refresh-token/')
        ) {
          // console.log('[Response Processing] Checking for tokens in response data')

          // Function to ensure token is clean without quotes
          const cleanTokenString = (token) => {
            if (typeof token === 'string') {
              // Remove quotes if present
              if (token.startsWith('"') && token.endsWith('"')) {
                return token.substring(1, token.length - 1)
              }
            }
            return token
          }

          // Clean access token if present
          if (response.data.data.access) {
            const originalToken = response.data.data.access
            response.data.data.access = cleanTokenString(originalToken)

            if (originalToken !== response.data.data.access) {
              // console.log('[Response Processing] Cleaned access token in API response')
            }
          }

          // Clean refresh token if present
          if (response.data.data.refresh) {
            const originalRefreshToken = response.data.data.refresh
            response.data.data.refresh = cleanTokenString(originalRefreshToken)

            if (originalRefreshToken !== response.data.data.refresh) {
              // console.log('[Response Processing] Cleaned refresh token in API response')
            }
          }
        }
      }

      return response
    })
    .catch((error) => {
      if (!error.response) {
        // If no response is received (network issues, no server reachable)
        console.error(`Network error or no response received for ${method} ${url}`, error.message)
        // Return a structured error or re-throw based on needs
        // For now, returning a simplified error object:
        return Promise.reject({
          request: error.request,
          message: error.message || 'Network error or no response from server.',
          isAxiosError: true,
        })
      }

      // Log the error response
      console.error(`Error with request ${method} ${url}`, error.response)

      // Handle 500 server errors for critical paths with fallback data
      if (error.response.status === 500) {
        // Provide fallback data for critical endpoints to prevent app crashes
        // if (url.includes('class_search/classbanner')) {
        //   console.warn(`Providing fallback data for failed endpoint: ${url}`)
        //   return Promise.resolve({
        //     data: {
        //       data: [],
        //       error: null,
        //     },
        //     status: 200,
        //     statusText: 'OK (Fallback)',
        //   })
        // }

        // Add fallback for job search endpoints
        if (url.includes('job_search/jobs')) {
          console.warn(
            `[Public Search] Providing fallback data for failed job search endpoint: ${url}`
          )
          return Promise.resolve({
            data: {
              data: {
                results: [],
                count: 0,
                pagination: { total_pages: 0, current_page: 1 },
              },
              error: 'Backend temporarily unavailable',
            },
            status: 200,
            statusText: 'OK (Job Search Fallback)',
          })
        }

        // Add fallback for tutor search endpoints
        if (url.includes('class_search') && !url.includes('classbanner')) {
          console.warn(
            `[Public Search] Providing fallback data for failed tutor search endpoint: ${url}`
          )
          return Promise.resolve({
            data: {
              data: {
                results: [],
                count: 0,
                pagination: { total_pages: 0, current_page: 1 },
              },
              error: 'Backend temporarily unavailable',
            },
            status: 200,
            statusText: 'OK (Tutor Search Fallback)',
          })
        }
      }

      // Handle specific cases like token expiration or unauthorized access
      if (error.response?.code === 'token_not_valid' || error.response.status === 401) {
        // console.log(`401 Unauthorized for ${method} ${url} - checking registration context`)

        // REGISTRATION FLOW: Special handling for PUT user/user/ during registration
        if (method === 'PUT' && url.includes('/user/user/')) {
          // This is likely during registration - get the current registration step
          const registerStepOne = store.get('register_step') || 1 // Renamed to avoid conflict

          if (registerStepOne >= 2) {
            // console.log(
            //   'Registration in progress (step ' + registerStepOne + ') - trying to recover tokens'
            // )

            // Try all possible sources for the token in order of reliability
            let recoveredToken = window.tempAuthToken || window.currentSubmissionToken

            // If no window token, try cookie (registration-specific cookies)
            if (!recoveredToken) {
              const tokenCookie = document.cookie
                .split('; ')
                .find((row) => row.startsWith('access_token='))
              if (tokenCookie) {
                recoveredToken = tokenCookie.split('=')[1]
              }
            }

            // Last resort - try localStorage
            if (!recoveredToken) {
              recoveredToken = localStorage.getItem('access_token')
            }

            if (recoveredToken) {
              // console.log('Token recovered from alternate source - retrying request')

              // Retry the request with the recovered token
              return axiosApi.request({
                headers: {
                  ...headers,
                  ApoloappAuth: `Authorization ${recoveredToken}`,
                },
                method,
                url,
                data,
                params,
              })
            } else {
              // console.warn(
              //   'All token recovery methods failed for registration. Using fallback approach...'
              // )

              error.response.registrationTokenIssue = true
              return Promise.reject(error.response)
            }
          }
        }

        // CRITICAL: Special handling for common endpoints that should not trigger login redirects
        const nonCriticalEndpoints = [
          '/user/user/',
          '/student_dashboard/',
          '/tutor_dashboard/',
          '/address/country/',
          '/address/language/',
          '/address/timezone/',
        ]

        const isNonCriticalEndpoint = nonCriticalEndpoints.some((endpoint) =>
          url.includes(endpoint)
        )

        if (isNonCriticalEndpoint) {
          // console.log(`Detected 401 on ${url} - this is a non-critical endpoint, NOT redirecting`)
          return Promise.reject(error.response)
        }

        const isActivationPath = url.includes('/activate/')
        const isLoginPath = url.includes('/user/login/')
        const isRegisterPath = url.includes('/user/register/')
        const isCurrentPathActivation = window.location.pathname.startsWith('/activate/')
        const isCurrentPathLogin = window.location.pathname.includes('/login')
        const isCurrentPathRegister = window.location.pathname.includes('/register')

        if (isActivationPath || isRegisterPath || isCurrentPathActivation || isCurrentPathLogin) {
          // console.log(
          //   `Skipping login redirect for auth error on ${url} because we're on a public route`
          // )
          return Promise.reject(error.response)
        }

        // Handle 401 errors during registration differently
        const isRegistrationContext =
          store.get('register_step') > 1 && store.get('register_step') < 8 // Renamed to avoid conflict
        const currentRegisterStepLogic = store.get('register_step') || 1 // Renamed to avoid conflict

        if (isRegistrationContext) {
          // ENHANCED REGISTRATION FLOW HANDLING:
          // 1. If on any registration step and the failed request is for user data,
          //    DO NOT redirect to login
          // 2. Special handling for step 3 (About.jsx) which handles its own errors
          const isUserDataRequest = method === 'GET' && url.includes('/user/user/')
          const isAboutStep = parseInt(currentRegisterStepLogic, 10) === 3

          if (isUserDataRequest) {
            console.warn(
              `[Request Error Handler] Authentication error during GET user/user/ on registration step ${currentRegisterStepLogic}. NOT redirecting to login. Error will propagate.`
            )
            return Promise.reject(error.response) // Let the error propagate
          }

          if (isAboutStep && url.includes('/user/')) {
            console.warn(
              `[Request Error Handler] Auth error during About step (step 3). NOT redirecting to login. Error will propagate.`
            )
            return Promise.reject(error.response) // Let the error propagate
          }

          // For other errors during registration, or other steps, only redirect if tokens are completely gone
          console.warn(
            `[Request Error Handler] Authentication error during registration (step ${currentRegisterStepLogic}).`
          )

          // Check if we still have tokens somewhere
          const hasTokensAnywhere =
            localStorage.getItem('access_token') ||
            localStorage.getItem('refresh_token') ||
            store.get('access_token') ||
            store.get('refresh_token')

          if (!hasTokensAnywhere) {
            console.warn(
              `[Request Error Handler] No tokens found. Clearing token stores and redirecting to login.`
            )
            store.set('access_token', null)
            store.set('refresh_token', null)
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')

            window.location.href = '/login'
          }
          return Promise.reject(error.response)
        }

        if (url.includes('/user/')) {
          console.warn(`Token error for ${url}. Checking token availability...`)

          // Check if we have tokens in any storage location
          const tokenAccessToken = localStorage.getItem('access_token') || store.get('access_token')
          const tokenRefreshToken =
            localStorage.getItem('refresh_token') || store.get('refresh_token')

          if (tokenAccessToken || tokenRefreshToken) {
            // console.log('Found valid tokens - NOT redirecting to login despite 401 error')
            // We have tokens, so this might be a timing issue
            return Promise.reject(error.response)
          }
        }

        // Only for regular authenticated paths, clear user data and redirect to login
        console.warn('Token expired or invalid, redirecting to login...')
        store.set('access_token', null)
        store.set('refresh_token', null)
        store.set('user_data', null)

        // Don't reset registration step if we're in the middle of registration
        const currentRegisterStep = store.get('register_step') || 1
        if (currentRegisterStep <= 1) {
          store.set('register_step', 1)
          store.set('switch_step', 1)
        }

        // Check if we're already logged in or on a dashboard path
        const isDashboardPath =
          window.location.pathname.includes('/statistic') ||
          window.location.pathname.includes('/dashboard')

        // Only redirect if we're not on any special paths AND have no tokens at all
        const hasAnyTokens =
          localStorage.getItem('access_token') ||
          localStorage.getItem('refresh_token') ||
          store.get('access_token') ||
          store.get('refresh_token')

        if (
          !isActivationPath &&
          !isRegisterPath &&
          !isCurrentPathActivation &&
          !isDashboardPath &&
          !hasAnyTokens
        ) {
          // console.log('No tokens found and not on a special path - redirecting to login')
          window.location.href = '/login'
        } else {
          // console.log('Tokens found or on special path - NOT redirecting to login despite 401')
        }

        // Return a rejected promise to stop further processing in the caller
        return Promise.reject(error.response)
      }

      // Return the error response for other cases
      return Promise.reject(error.response) // Reject promise on catch
    })
}

export default request
